import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ChevronRight, ChevronDown, Users, ShieldCheck, User as UserIcon, Pencil, UserCog, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Flatten the nested tree into a display list of { node, depth, hasKids, isLast[] }
// Iterative — no recursion, no self-referential JSX component.
function flatten(tree, collapsed) {
  const rows = [];
  const stack = [];
  for (let i = tree.length - 1; i >= 0; i--) stack.push({ n: tree[i], d: 0 });
  while (stack.length) {
    const { n, d } = stack.pop();
    const kids = n.reports || [];
    const open = !collapsed[n.id];
    rows.push({ node: n, depth: d, hasKids: kids.length > 0, isOpen: open });
    if (open) {
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ n: kids[i], d: d + 1 });
    }
  }
  return rows;
}

function collectDescendants(users, rootId) {
  const set = new Set();
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.shift();
    for (const u of users) {
      if (u.manager_id === cur && !set.has(u.id)) {
        set.add(u.id);
        queue.push(u.id);
      }
    }
  }
  return set;
}

function roleIcon(name) {
  if (name === "Admin") return <ShieldCheck className="w-4 h-4 text-indigo-600" />;
  if ((name || "").includes("Manager")) return <UserCog className="w-4 h-4 text-sky-600" />;
  return <UserIcon className="w-4 h-4 text-slate-400" />;
}

export default function OrgChart() {
  const { user: me } = useAuth();
  const [tree, setTree] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [editUser, setEditUser] = useState(null);

  const load = async () => {
    try {
      const t = await api.get("/users/org-chart");
      const u = await api.get("/users");
      const r = await api.get("/roles");
      setTree(t.data);
      setUsers(u.data);
      setRoles(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (me && me.role === "admin") load(); }, [me]);
  if (me && me.role !== "admin") return <Navigate to="/dashboard" replace />;

  const rows = flatten(tree, collapsed);
  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  const totalCount = users.length;
  const adminCount = users.filter((u) => u.role_name === "Admin").length;
  const withMgr = users.filter((u) => u.manager_id).length;
  const orphans = users.filter((u) => !u.manager_id && u.role_name !== "Admin").length;

  return (
    <div data-testid="org-chart-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Access</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Org Chart</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">Every employee, their job title, permission level, and who they report to. Data visibility on Leads, Clients and Quotations follows this tree.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="border rounded-lg px-4 py-3 bg-white border-slate-200">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Employees</div>
          <div className="text-xl font-semibold" data-testid="stat-employees">{totalCount}</div>
        </div>
        <div className="border rounded-lg px-4 py-3 bg-white border-slate-200">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">With a manager</div>
          <div className="text-xl font-semibold">{withMgr}</div>
        </div>
        <div className="border rounded-lg px-4 py-3 bg-indigo-50 border-indigo-100 text-indigo-800">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Admins</div>
          <div className="text-xl font-semibold">{adminCount}</div>
        </div>
        <div className={"border rounded-lg px-4 py-3 " + (orphans ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-white border-slate-200")}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Top-level (no manager)</div>
          <div className="text-xl font-semibold">{orphans}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-2" data-testid="org-tree">
        {rows.length === 0 ? (
          <div className="text-center text-slate-400 py-10">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No employees yet.
          </div>
        ) : (
          <ul>
            {rows.map((row) => {
              const n = row.node;
              const kids = n.reports || [];
              return (
                <li key={n.id} data-testid={"org-node-" + n.id}
                    className="group flex items-center gap-2 py-2 pr-2 rounded-md hover:bg-slate-50"
                    style={{ paddingLeft: (row.depth * 20 + 4) + "px" }}>
                  {row.hasKids ? (
                    <button type="button" onClick={() => toggle(n.id)}
                            className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100"
                            data-testid={"toggle-" + n.id}>
                      {row.isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  ) : (
                    <span className="w-6 h-6" />
                  )}
                  {roleIcon(n.role_name)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 text-sm truncate">{n.name}</span>
                      {n.role_name ? (
                        <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-600">{n.role_name}</Badge>
                      ) : null}
                      {n.is_active === false ? (
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500 bg-slate-50">Deactivated</Badge>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {n.job_title ? n.job_title : <span className="italic text-slate-400">no job title</span>}
                      <span className="text-slate-300"> · </span>
                      <span className="font-mono">{n.email}</span>
                      {row.hasKids ? (
                        <span className="ml-2 text-[11px] text-slate-400">{kids.length} report{kids.length === 1 ? "" : "s"}</span>
                      ) : null}
                    </div>
                  </div>
                  <button type="button" onClick={() => setEditUser(n)}
                          className="text-slate-400 hover:text-indigo-600 p-1"
                          data-testid={"edit-" + n.id}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <EditEmployeeDialog user={editUser} users={users} roles={roles}
                          onClose={() => setEditUser(null)}
                          onSaved={() => { setEditUser(null); load(); }} />
    </div>
  );
}

function EditEmployeeDialog({ user, users, roles, onClose, onSaved }) {
  const [jobTitle, setJobTitle] = useState("");
  const [roleId, setRoleId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) {
      setJobTitle(user.job_title || "");
      setRoleId(user.role_id || "");
      setManagerId(user.manager_id || "");
    }
  }, [user]);
  if (!user) return null;
  const desc = collectDescendants(users, user.id);
  const managerChoices = users.filter((u) => u.id !== user.id && !desc.has(u.id));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/" + user.id, { job_title: jobTitle, role_id: roleId, manager_id: managerId });
      toast.success("Employee updated");
      if (onSaved) onSaved();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-employee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-600" />
            {user.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Job title</Label>
            <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
                   placeholder="e.g. Regional Sales Manager" data-testid="edit-job-title" className="mt-1" />
            <p className="text-xs text-slate-500 mt-1">Free-text label. Doesn't affect permissions.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Permission level</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger className="mt-1" data-testid="edit-role"><SelectValue placeholder="Pick a permission level" /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}{r.is_system ? " (system)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Add new tiers on the Roles page.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reports to</Label>
            <Select value={managerId || "__none__"} onValueChange={(v) => setManagerId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="edit-manager"><SelectValue placeholder="No manager" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No manager (top of tree)</SelectItem>
                {managerChoices.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}{u.job_title ? " · " + u.job_title : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Descendants hidden to avoid cycles.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-save">
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
